// @flow
import React, { useState, useRef, useEffect } from 'react';
import type { Node } from 'react';
import ClassNames from 'classnames';
import Autocomplete from 'react-google-autocomplete';
import * as SVG_Icons from './svg_icons';
import { isEnterKey } from './careerPageUtils';

// Text
type TextProps = {
	children: Node,
	size?: 1 | 2 | 3 | 4 | 5 | 6,
	color?: 'light' | 'dark' | 'darker',
	boldness: 'light' | 'medium' | 'bold',
	classNames?: string,
	clickable: boolean,
};

export function CS_Text(props: TextProps) {
	const { classNames, children, size = 4, color = 'dark', boldness = 'light', clickable } = props;
	return (
		<span
			className={ClassNames(
				'rb-text',
				`rb-text-${size}`,
				`rb-text--color-${color}`,
				`rb-text--weight-${boldness}`,
				classNames,
				{
					'rb-text--clickable': clickable,
				},
			)}>
			{children}
		</span>
	);
}

CS_Text.defaultProps = {
	size: 4,
	color: 'dark',
	boldness: 'light',
};

// Grid
type SpaceProps = {
	children?: Node,
	size?: 0 | 1 | 2 | 3 | 4 | 5 | 6,
	size_desktop?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 12, // when you want particular size only for desktop
	vertical?: boolean,
};

export function CS_Space(props: SpaceProps) {
	const { size = 4, size_desktop = 0, vertical, children } = props;

	if (children) {
		const classes = ClassNames(`rb-space--around-${size}rbpx`, {
			[`rb-space--around-${size_desktop}rbpx`]: size_desktop,
		});

		return <div className={classes}>{children}</div>;
	}

	const classes = ClassNames(`rb-offset-${size}rbpx`, {
		[`rb-offset-desktop-${size_desktop}rbpx`]: size_desktop,

		[`rb-space-on-bottom-${size}rbpx`]: vertical,
		[`rb-space-on-bottom-desktop-${size_desktop}rbpx`]: size_desktop && vertical,

		'rb-block': vertical,
	});

	return <span className={classes} />;
}

CS_Space.defaultProps = {
	size: 4,
};

// Form
type CheckboxProps = {
	children: Node,
};

export function CS_Checkbox(props: CheckboxProps) {
	const { children, ...element_props } = props;

	return (
		<label className={ClassNames('rb-checkbox rb-checkbox--block')}>
			<input className="rb-checkbox__input" type="checkbox" {...element_props} />
			<div className="rb-checkbox__box" />
			<CS_Text classNames="rb-checkbox__label" size={5}>
				{children}
			</CS_Text>
		</label>
	);
}

type InputBoxProps = {
	full_width?: boolean,
	classNames?: string,
};

export function CS_Input(props: InputBoxProps) {
	const { full_width, classNames, ...element_props } = props;
	const classes = ClassNames('rb-input', classNames);
	const wrapper_classes = ClassNames('rb-input-wrapper', {
		'rb-input-wrapper--full-width': full_width,
	});

	return (
		<div className={wrapper_classes}>
			<input className={classes} autoComplete="off" {...element_props} />
		</div>
	);
}

export const CS_ScrollableContainer = (props: { children: Node }) => {
	const classes = ClassNames('rb-scrollable-area', 'rb-scrollable-area--size-md');
	return <div className={classes}>{props.children}</div>;
};

type AutoCompletePlacesProps = {
	onPlaceSelected: () => {},
	defaultValue: string,
	onIconClick: () => {},
	is_loading: boolean,
};

export function CS_AutoCompletePlaces(props: AutoCompletePlacesProps) {
	const { is_loading } = props;
	const classes = ClassNames('rb-input js-location-autocomplete');
	const wrapper_classes = ClassNames('rb-input-wrapper js-location-autocomplete-wrapper');
	const icon_classes = ClassNames('rb-input__icon rb-input__icon--position-right rb-input__button-right');

	if (window.google) {
		return (
			<div
				className={wrapper_classes}
				onKeyDown={e => {
					if (isEnterKey(e)) e.preventDefault();
				}}>
				<Autocomplete
					onPlaceSelected={props.onPlaceSelected}
					style={{ width: '100%' }}
					className={classes}
					types={['(regions)']}
					defaultValue={props.defaultValue}
					name="loc"
					maxLength={100}
				/>
				<button
					data-toggle="tooltip"
					data-placement="top"
					title="Use my location"
					type="button"
					className={icon_classes}
					disabled={is_loading}
					onClick={() => props.onIconClick()}>
					<i
						aria-hidden="true"
						className={ClassNames('fa', {
							'fa-location-arrow': !is_loading,
							'fa-spinner fa-spin': is_loading,
						})}
					/>
				</button>
			</div>
		);
	}
	return null;
}

type ToggleSwitchProps = {
	value: string,
	options: Array<{ key: string, name: string }>,
	input_key: string,
	disabled: boolean,
};

export function CS_ToggleSwitch(props: ToggleSwitchProps) {
	const { value, options, input_key, disabled } = props;
	const [radius_unit, onSearchChange] = useState(value);
	const classes = ClassNames('button-group', {
		'rb-input--disabled': disabled,
	});
	return (
		<div className={classes}>
			<input type="hidden" value={radius_unit} name={input_key} hidden />
			{options.map(({ name, key }) => (
				<button
					key={key}
					className={'button-group__button ' + (value === key ? 'button-group__button--active' : '')}
					onClick={e => onSearchChange(key)}>
					{name}
				</button>
			))}
		</div>
	);
}

CS_ToggleSwitch.defaultProps = {
	value: 'mile',
};

function getPositionForSliderText($input_slider, value, min, max) {
	if (!$input_slider) return {};
	const width = $input_slider.offsetWidth;
	const new_point = (value - min) / (max - min);
	const offset = -1.3;

	if (new_point < 0)
		return {
			left: 0,
			marginLeft: `${offset}%`,
		};

	if (new_point > 1)
		return {
			left: width,
			marginLeft: `${offset}%`,
		};

	return {
		left: width * new_point + offset,
		marginLeft: `${offset - new_point}%`,
	};
}

type InputRangeProps = {
	min: number,
	max: number,
	defaultValue: number,
	disabled: boolean,
};

export function CS_InputRange(props: InputRangeProps) {
	const { defaultValue = 5, min, max, disabled, ...element_props } = props;
	const classes = ClassNames('rb-input-slider rb-input--borderless p-0', {
		'rb-input--disabled': disabled,
	});

	const [value, setValue] = useState(defaultValue);
	const [isMounted, setMounted] = useState(false);
	let $input_slider = useRef(null);
	useEffect(() => {
		setMounted(true);
	}, []);

	return (
		<div className="rb-input-wrapper">
			<input
				ref={$input_slider}
				defaultValue={defaultValue}
				type="range"
				className={classes}
				min={min}
				max={max}
				onChange={e => setValue(e.target.value)}
				disabled={disabled}
				{...element_props}
			/>
			{isMounted && (
				<div
					className={ClassNames('rb-text-5 rb-input-range-value', { hide: disabled })}
					style={getPositionForSliderText($input_slider.current, value, min, max)}>
					{value}
				</div>
			)}
		</div>
	);
}

type CSLabelProps = {
	data: { name: string, id: string },
	onRemove: (id: string) => {},
};

export function CS_Label(props: CSLabelProps) {
	const { data, onRemove } = props;
	const { name, id } = data;
	return (
		<span className="rb-label" data-toggle="tooltip" data-placement="top" title={name}>
			<span className="rb-label__text">{name.length > 30 ? `${name.substring(0, 30)}...` : name}</span>
			<button type="button" className="rb-label__button" onClick={() => onRemove(id)}>
				{SVG_Icons.close_icon}
			</button>
		</span>
	);
}

export const CS_Row = (props: CSRowProps) => {
	const {
		children,
		className,
		inline,
		wrap,
		center,
		vcenter,
		space_between,
		vEnd,
		vBaseline,
		full_width,
		right_aligned,
		top_aligned,
		bottom_aligned,
		gutter,
		direction_column,
		ellipsis,
		direction_row_reverse,
		...element_props
	} = props;
	const classes = ClassNames(className, {
		'rb-row': !inline,
		'rb-row--inline': inline,
		'rb-row--wrap': wrap,
		'rb-row--right': right_aligned,
		'rb-row--center': center,
		'rb-row--vertical-center': vcenter,
		'rb-row--space-between': space_between,
		'rb-row--vertical-end': vEnd,
		'rb-row--vertical-baseline': vBaseline,
		'rb-row--top': top_aligned,
		'rb-row--bottom': bottom_aligned,
		'rb-row-gutter': gutter,
		'rb-row--direction-column': direction_column,
		'rb-row--full-width': full_width,
		'rb-row--ellipsis': ellipsis,
		'rb-row--direction-row-reverse': direction_row_reverse,
	});
	return (
		<div className={classes} {...element_props}>
			{children}
		</div>
	);
};

type CSRowProps = {
	children: Node,
	wrap: boolean,
	right_aligned: boolean,
	center: boolean,
	vcenter: boolean,
	space_between: boolean,
	vEnd: boolean,
	vBaseline: boolean,
	top_aligned: boolean,
	bottom_aligned: boolean,
	gutter: boolean,
	direction_column: boolean,
	inline: boolean,
	ellipsis: boolean,
	direction_row_reverse: boolean,
	full_width: boolean,
	className: string,
};

CS_Row.defaultProps = {
	wrap: true,
};

export const CS_Col = (props: ColProps) => {
	const {
		size,
		max_size,
		right_aligned,
		children,
		className,
		gutter,
		full_height,
		ellipsis,
		...element_props
	} = props;
	const classes = ClassNames(className, {
		[`rb-col-${size}`]: size,
		[`rb-col-max-size-${max_size}`]: max_size,
		'rb-col--right': right_aligned,
		'rb-col-gutter': gutter,
		'rb-full-height': full_height,
		'rb-col-ellipsis': ellipsis,
	});

	return (
		<div className={classes} {...element_props}>
			{children}
		</div>
	);
};

CS_Col.defaultProps = {
	size: 5,
};

type ColProps = {
	children: Node,
	right_aligned: boolean,
	size?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10,
	max_size: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10,
	gutter: boolean,
	full_height: boolean,
	className: string,
	ellipsis: boolean,
};

type RadioProps = {
	children: Node,
};

export function CS_Radio(props: RadioProps) {
	const { children, ...element_props } = props;

	return (
		<div className="radio">
			<label>
				<input type="radio" {...element_props} />
				<CS_Text>{children}</CS_Text>
			</label>
		</div>
	);
}

type DropdownProps = {
	children: Node,
	id: string,
	value: string,
};
export function CS_Dropdown(props: DropdownProps) {
	const { id, value, children } = props;
	return (
		<span className="dropdown">
			<button
				className="btn btn-default dropdown-toggle"
				type="button"
				id={id}
				data-toggle="dropdown"
				aria-haspopup="true"
				aria-expanded="false">
				{value}
				<CS_Space size={2} />
				<span className="dropdown-icon">{SVG_Icons.chevron_down}</span>
			</button>
			<div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
				{children}
			</div>
		</span>
	);
}

type PaginationProps = {
	prev_page_url?: string,
	next_page_url?: string,
	start_index: number,
	end_index: number,
	total_results: number,
};
export function CS_Pagination(props: PaginationProps) {
	const { prev_page_url, next_page_url, start_index, end_index, total_results } = props;
	const prev_button_classes = ClassNames('page-item', {
		disabled: !prev_page_url,
	});
	const next_button_classes = ClassNames('page-item', {
		disabled: !next_page_url,
	});

	function replaceAmpersAnd(string) {
		return string.replace(/amp;/g, '');
	}
	return (
		<span>
			<ul className="pagination">
				<li className={prev_button_classes}>
					<a
						className="page-link rb-row--vertical-center rb-row"
						href={prev_page_url ? `/${replaceAmpersAnd(prev_page_url)}#content` : ''}>
						<span>{SVG_Icons.chevron_left_small}</span>
						<CS_Space size={2} />
						<CS_Text>Prev</CS_Text>
					</a>
				</li>
				<li className="page-item pagination-info">
					<CS_Text>
						{start_index} - {end_index} of {total_results} Jobs
					</CS_Text>{' '}
				</li>
				<li className={next_button_classes}>
					<a
						className="page-link rb-row--vertical-center rb-row"
						href={next_page_url ? `/${replaceAmpersAnd(next_page_url)}#content` : ''}>
						<CS_Text> Next </CS_Text>
						<CS_Space size={2} />
						<span>{SVG_Icons.chevron_right_small}</span>
					</a>
				</li>
			</ul>
		</span>
	);
}
